import React, { useEffect } from "react";
import { Badge, Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Autocomplete from "../../components/shared/Autocomplete";
import Button from "../../components/shared/Button";
import PageHeader from "../../components/shared/PageHeader";
import Table from "../../components/shared/Table";
import { Constant } from "../../lib/Constant";
import ContainerComponent from "../../components/shared/ContainerComponent";
import { pxtoRem } from "../../lib/helper";
import useChapters from "../../hooks/chapter/useChapters";
import {
  useCountries,
  useExamBoards,
  useSubjects,
  useYears,
} from "../../hooks/chapter";
import { Spinner } from "../../components/shared/Spinner";
import Empty from "../../components/shared/Empty";
import { useDispatch, useSelector } from "react-redux";
import { setPageChange } from "../../redux-toolkit/table";
import { setFilter, setFormData } from "../../redux-toolkit/worksheet";
import Toggle from "../../components/shared/Toggle";
import { useState } from "react";
import UploadFile from "../../components/shared/popups/UploadFile";
import {
  useWorksheets,
  useUploadWorksheetFile,
  useChangeWorksheetStatus,
  useChapterFilters,
} from "../../hooks/worksheet";
import { toast } from "react-toastify";
import { theme } from "../../lib/themeConstants";
import { useQueryClient } from "react-query";
import { setQuestionsData } from "../../redux-toolkit/questions";

const StyledRoundIcon = styled.div`
  height: ${pxtoRem(60)};
  width: ${pxtoRem(60)};
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.NEBULA_BLUE};
  opacity: 0.5;
  flex: 0 0 ${pxtoRem(60)};
`;

const StyledChapterNameContainer = styled.div`
  display: flex;
  padding-left: ${pxtoRem(20)};
  padding-right: ${pxtoRem(20)};
  align-items: center;
`;

const StyledChapterName = styled.div`
  margin-left: ${pxtoRem(40)};
`;

const StyledBadge = styled(Badge)`
  background-color: ${(props) => props.color} !important;
  width: ${pxtoRem(146)};
  height: ${pxtoRem(46)};
  padding-top: ${pxtoRem(13)};
  font-size: ${pxtoRem(20)};
  border-radius: ${pxtoRem(10)};
  color: ${(props) => props.theme.colors.WHITE};
  font-weight: normal;
`;

const StyledButton = styled(Button)`
  height: ${pxtoRem(46)};
  width: ${pxtoRem(67)};
  font-size: ${pxtoRem(20)};
  /* line-height: ${pxtoRem(18)}; */
  padding: 0;
  &:disabled {
    background-color: ${(props) => props.theme.colors.BRIGHT_GREY};
    border-color: ${(props) => props.theme.colors.BRIGHT_GREY};
    color: ${(props) => props.theme.colors.METALIC_SILVER};
  }
`;

const StyledUploadFile = styled.img`
  height: ${(props) => (props?.hasFile ? pxtoRem(32) : pxtoRem(21))};
  width: ${(props) => (props?.hasFile ? pxtoRem(32) : pxtoRem(21))};
  cursor: pointer;
`;
function Worksheet() {
  const dispatch = useDispatch();
  const [uploadFileModal, setUploadFileModal] = useState(false);
  const [fileData, setFileData] = useState(null);

  const currentPage = useSelector((state) => {
    return state.table.page;
  });

  const queryClient = useQueryClient();

  const { country, examboard, subject, year, chapter } = useSelector(
    (state) => {
      return state.worksheet.filter;
    }
  );

  const {
    data: worksheets,
    error,
    isFetching,
  } = useWorksheets(
    country?.value,
    examboard?.value,
    subject?.value,
    year?.value,
    chapter?.value,
    currentPage
  );

  const uploadFileMutation = useUploadWorksheetFile();
  const statusMutation = useChangeWorksheetStatus();

  const { data: countriesData, isFetching: countriesLoading } = useCountries();
  const { data: examboardData, isFetching: examLoading } = useExamBoards();
  const { data: subjectsData, isFetching: subjectsLoading } = useSubjects();
  const { data: gradesData, isFetching: gradesLoading } = useYears();
  const { data: chaptersData, isFetching: chaptersLoading } = useChapterFilters(
    country?.value,
    examboard?.value,
    subject?.value,
    year?.value
  );

  useEffect(() => {
    dispatch(setFormData(null));
    dispatch(setQuestionsData(null));
  });

  useEffect(() => {
    if (error) {
      toast.error(error?.response?.data?.message);
    }
    return () => {
      dispatch(setPageChange(1));
    };
  }, [dispatch, error]);
  const navigate = useNavigate();

  const formatFilterOptions = (data) => {
    let formattedData = data?.map((item) => {
      return { value: item.id, label: item.name };
    });
    return formattedData;
  };

  const onFilterChange = (key, value) => {
    let data = { country, examboard, subject, year };
    data[key] = value;
    dispatch(setFilter(data));
  };

  const confirmUploadFile = async (data) => {
    let formData = new FormData();

    if (typeof data?.image !== "string" && data?.file?.length > 0) {
      formData.append("file", data?.file?.[0]);
      formData.append("id", fileData.id);
    } else {
      formData.append("delete", true);
      formData.append("id", fileData.id);
    }
    uploadFileMutation.mutate(formData, {
      onError: (res) => {
        toast.error(res?.response?.data?.message);
        setUploadFileModal(false);
      },
      onSuccess: (res) => {
        queryClient.invalidateQueries("worksheets", {
          country_id: country?.value,
          exam_board_id: examboard?.value,
          subject_id: subject?.value,
          year_id: year?.value,
          chapter_id: chapter?.value,
          page: currentPage,
        });
        setUploadFileModal(false);
      },
    });
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Chapter name",
        accessor: "chapter_name",
        customWidth: 30,
        Cell: ({ row }) => (
          <StyledChapterNameContainer>
            <StyledRoundIcon />
            <StyledChapterName>
              {" "}
              {row?.original?.chapter?.name}
            </StyledChapterName>
          </StyledChapterNameContainer>
        ),
      },
      {
        Header: "Subject",
        accessor: "subject",
        Cell: ({ row }) => (
          <StyledBadge
            color={
              row?.original?.chapter?.subject?.color &&
              row?.original?.chapter?.subject?.color
            }
          >
            {row?.original?.chapter?.subject?.name &&
              row.original?.chapter?.subject.name}
          </StyledBadge>
        ),
      },
      {
        Header: "Upload file",
        accessor: "uploadFile",
        Cell: ({ row }) => (
          <StyledUploadFile
            src={
              row?.original?.file
                ? Constant.Icons.file
                : Constant?.Icons.upload_outline
            }
            onClick={() => {
              setUploadFileModal(true);
              setFileData({
                id: row?.original?.id,
                file: row?.original?.file ? row?.original?.file : null,
              });
            }}
            hasFile={row?.original?.file}
            alt="upload file"
          />
        ),
      },
      {
        Header: "Grade",
        accessor: "grade",
        Cell: ({ row }) => (
          <span>
            {row?.original?.chapter?.years_data &&
              row?.original?.chapter?.years_data
                .map(function (item) {
                  return item.name;
                })
                .join(",")}
          </span>
        ),
      },
      {
        Header: "Exam board",
        accessor: "exam_board",
        Cell: ({ row }) => (
          <span>
            {row?.original?.chapter?.exam_board?.name &&
              row.original?.chapter?.exam_board.name}
          </span>
        ),
      },
      {
        Header: "Action",
        Cell: ({ row }) => (
          <StyledButton
            text="View"
            disabled={row.original.questions_count > 0 ? false : true}
            onClick={() => {
              navigate(`view/${row?.original?.id}`);
            }}
            bgColor={theme.colors.NEBULA_BLUE}
          />
        ),
      },
      {
        Header: "",
        accessor: "status",
        customWidth: 17,
        Cell: ({ row }) => (
          <Toggle
            checked={row.original?.status === "publish" ? true : false}
            onChange={(status) => {
              const formData = new FormData();
              formData.set("status", status ? "publish" : "draft");
              formData.set("id", row?.original?.id);
              statusMutation.mutate(formData, {
                onError: (res) => {
                  toast.error(res?.response?.data?.message);
                },
                onSuccess: (res) => {
                  queryClient.invalidateQueries("worksheets", {
                    country_id: country?.value,
                    exam_board_id: examboard?.value,
                    subject_id: subject?.value,
                    year_id: year?.value,
                    chapter_id: chapter?.value,
                    page: currentPage,
                  });
                },
              });
            }}
            onLable={"Publish"}
            offLable={"Draft"}
          />
        ),
      },
    ],
    // eslint-disable-next-line
    []
  );
  return (
    <>
      <Spinner
        isLoading={
          countriesLoading ||
          examLoading ||
          subjectsLoading ||
          gradesLoading ||
          chaptersLoading ||
          isFetching ||
          statusMutation?.isLoading
        }
      />
      <PageHeader
        title="Worksheet"
        control={
          <Button
            text="Create"
            icon={Constant.Icons.add_circle_outline_white}
            bgColor={theme.colors.NEBULA_BLUE}
            onClick={() => {
              navigate("create");
            }}
          />
        }
      ></PageHeader>

      <ContainerComponent>
        <Row>
          <Col>
            <Autocomplete
              options={formatFilterOptions(countriesData)}
              value={country}
              onChange={(data) => {
                // setCountry(data);
                onFilterChange("country", data);
                dispatch(setPageChange(1));
              }}
              placeholder="Country"
              className="m-2 auto-complete"
            />
          </Col>
          <Col>
            <Autocomplete
              options={formatFilterOptions(examboardData)}
              value={examboard}
              onChange={(data) => {
                // setExamboard(data);
                onFilterChange("examboard", data);
                dispatch(setPageChange(1));
              }}
              placeholder="Examboard"
              className="m-2 auto-complete"
            />
          </Col>
          <Col>
            <Autocomplete
              options={formatFilterOptions(subjectsData)}
              value={subject}
              onChange={(data) => {
                // setSubject(data);
                onFilterChange("subject", data);
                dispatch(setPageChange(1));
              }}
              placeholder="Subject"
              className="m-2 auto-complete"
            />
          </Col>
          <Col>
            <Autocomplete
              options={formatFilterOptions(gradesData)}
              value={year}
              onChange={(data) => {
                onFilterChange("year", data);
                dispatch(setPageChange(1));
              }}
              placeholder="Grade"
              className="m-2 auto-complete"
            />
          </Col>
          <Col>
            <Autocomplete
              options={formatFilterOptions(chaptersData)}
              value={chapter}
              onChange={(data) => {
                // setGrade(data);
                onFilterChange("chapter", data);
                dispatch(setPageChange(1));
              }}
              placeholder="Chapter"
              className="m-2 auto-complete"
            />
          </Col>
        </Row>
        <Row>
          <Col className="mt-3">
            {worksheets?.data?.length > 0 ? (
              <Table
                data={worksheets?.data}
                columns={columns}
                defaultPageSize={worksheets?.per_page}
                totalPages={worksheets?.last_page}
                currentPage={worksheets?.current_page}
              ></Table>
            ) : (
              !isFetching && (
                <Empty text={Constant.tableErrorMessages.worksheet} />
              )
            )}
          </Col>
        </Row>
        <UploadFile
          show={uploadFileModal}
          onConfirm={confirmUploadFile}
          onCancel={() => setUploadFileModal(false)}
          file={fileData?.file}
          loading={uploadFileMutation?.isLoading}
        />
      </ContainerComponent>
    </>
  );
}

export default Worksheet;
